import { CircularProgress, Box, Collapse, Divider, IconButton, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Fragment, useMemo, useState } from 'react';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { useServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { getFormattedDate, LabelValue, LabelValueProps } from '../../../task/common/TaskDetailDrawer';
import moment from 'moment';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';

interface ServiceRequestDetailsContainerProps {
  serviceRequestId: string | undefined;
}

export default function ServiceRequestDetailsContainer({
  serviceRequestId,
}: ServiceRequestDetailsContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { configurations } = useTenantConfigData();
  const { i18n } = useLocales();
  const [
    serviceRequest,
    {
      isFetching: isFetchingServiceRequest,
      refresh: refreshServiceRequest,
      update: updateServiceRequest,
    },
  ] = useServiceRequest(serviceRequestId, {
    map: ServiceRequestWrapper,
    autofetch: !!serviceRequestId,
  });

  const createdOn = useMemo(() => {
    if (!serviceRequest?.authoredOn) {
      return '-';
    }

    return getFormattedDate(moment(serviceRequest?.authoredOn));
  }, [serviceRequest]);

  const status = useMemo(() => {
    if (!serviceRequest?.status) {
      return '-';
    }

    return serviceRequest?.status;
  }, [serviceRequest]);

  const requesterDisplay = useMemo(() => {
    if (!serviceRequest?.requester?.display) {
      return '-';
    }

    return serviceRequest?.requester?.display;
  }, [serviceRequest]);

  const srDetails: LabelValueProps[] = [
    {
      label: 'No.',
      value: serviceRequest?.getInternalNumber(
        configurations?.fhir?.serviceRequestInternalNumberIdenitifierSystem
      ),
    },
    { label: 'Created On', value: createdOn },
    { label: 'Status', value: status },
    { label: 'Referred From', value: requesterDisplay },
    {
      label: 'Referred To',
      value: serviceRequest?.getPerformersByReferenceType('PractitionerRole')?.[0]?.display ||
             serviceRequest?.getPerformersByReferenceType('Practitioner')?.[0]?.display ||
             serviceRequest?.getPerformersByReferenceType('Organization')?.[0]?.display ||
             '-',
    },
    { label: 'Service', value: serviceRequest?.getServiceName() ?? '-' },
    { label: 'Location', value: serviceRequest?.getServiceLocationName() ?? '-' },
    { label: 'Assigned To', value: serviceRequest?.getServiceAssignedToName() ?? '-' },
  ];

  if (!serviceRequestId) {
    return null;
  }

  return (
    <Box>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.95rem',
            letterSpacing: '0.02em',
          }}
        >
          Service Request Details
        </Typography>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          size="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease-in-out',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
              color: 'primary.main',
            },
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <Box>
          {isFetchingServiceRequest ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {srDetails.map((detail) => (
                <Fragment key={detail.label}>
                  <LabelValue {...detail} />
                </Fragment>
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
